import { Auth0Client } from '@auth0/auth0-spa-js';
import type { Auth0ClientOptions } from '@auth0/auth0-spa-js';
import Cookies from 'cookies-js';
import React from 'react';
import { exchangeAuthorizationCodeOnce } from '@peloton/auth/exchangeAuthorizationCodeOnce';
import { toMatchingTld, SupportedTLD } from '@peloton/internationalize/models/locale';
import { getContainsLoginCookie, getLoggedInCookieName } from '@auth/utils/loginCookie';
import { AuthEnv } from '@auth/utils/types';
import type {
  AuthContextValue,
  AuthProviderEvent,
  AuthStatus,
  AuthUser,
  IdToken,
  LoginRedirectOpts,
  LogoutOpts,
} from '@auth/utils/types';
import { resolveReturnToFromAppState } from './resolveReturnToFromUrl';

const AUTH0_CLAIM_USER_ID = 'http://onepeloton.com/user_id';

/**
 * Backoff windows applied to the *per-request* token path after a failed
 * network silent-auth attempt. Escalates on each consecutive failure and is
 * reset by the next success.
 *
 * Why this exists: `getAccessToken()` is called from the Axios request
 * interceptor and the Apollo link, i.e. once per outbound request.
 * auth0-spa-js caches successes but has no negative cache, so a session that
 * *cannot* currently obtain a token re-runs the whole miss path — including a
 * `prompt=none` /authorize iframe — on every single request. When that iframe
 * can resolve it costs one Auth0 round trip per request; when it cannot resolve
 * at all it costs the full `authorizeTimeoutInSeconds` per request, because an
 * unregistered `redirect_uri` is served a plain HTML error page with no
 * `web_message` responder and `runIframe` then has no exit but its own timer.
 *
 * With a breaker, a broken configuration costs one doomed attempt per window
 * instead of one per request.
 */
const SILENT_AUTH_BACKOFF_MS = [30_000, 60_000, 300_000];

type SilentAuthBackoff = {
  /** Epoch ms before which the per-request path must not attempt the network. */
  blockedUntil: number;
  /** Consecutive failures, used to index SILENT_AUTH_BACKOFF_MS. */
  failures: number;
};

const NO_SILENT_AUTH_BACKOFF: SilentAuthBackoff = { blockedUntil: 0, failures: 0 };

/**
 * How long a request that arrives mid-initialization waits for the Auth0 client
 * before giving up and going out unauthenticated.
 *
 * Why bounded rather than unbounded: initialization can include a `prompt=none`
 * /authorize iframe whose worst case is `authorizeTimeoutInSeconds` (15s, see
 * createAuth0Client). An unbounded await would put that stall in front of every
 * request on a cold load, which is worse than the bug it fixes. 3s is
 * comfortably longer than a healthy init — a refresh-token grant is one round
 * trip — and short enough that a pathological init degrades to today's
 * behaviour (a guest request) rather than to a hung page.
 */
const DEFAULT_AUTH_INIT_WAIT_MS = 3_000;

type Deferred = { promise: Promise<void>; resolve: () => void };

const toDeferred = (): Deferred => {
  let resolve!: () => void;
  const promise = new Promise<void>(res => {
    resolve = res;
  });
  return { promise, resolve };
};

const toNextBackoff = ({ failures }: SilentAuthBackoff): SilentAuthBackoff => {
  const backoffMs =
    SILENT_AUTH_BACKOFF_MS[Math.min(failures, SILENT_AUTH_BACKOFF_MS.length - 1)];
  return { failures: failures + 1, blockedUntil: Date.now() + backoffMs };
};

// ---------------------------------------------------------------------------
// Auth0 client factory — preserves cookieDomain + cacheLocation: 'localstorage'
// ---------------------------------------------------------------------------

export const createAuth0Client = (initOptions: Auth0ClientOptions): Auth0Client => {
  const tld = toMatchingTld(window.location.hostname);
  return new Auth0Client({
    ...initOptions,
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    // If a client is misconfigured in the auth0 tenant it can take 60 seconds for getTokenSilently to resolve
    authorizeTimeoutInSeconds: initOptions?.authorizeTimeoutInSeconds ?? 15,
    cookieDomain: `.onepeloton${tld}`,
  });
};

// ---------------------------------------------------------------------------
// LogoutCookie — moved inside AuthProvider (no separate provider needed)
// ---------------------------------------------------------------------------

const setLogoutCookie = (authEnv: AuthEnv) => {
  const currentPathname = location.origin;
  const cookieName = getLoggedInCookieName(authEnv);
  const currentCookie = Cookies.get(cookieName);
  if (!currentCookie || currentCookie.includes(currentPathname)) {
    return;
  }
  const newCookie = [currentCookie, currentPathname];
  Cookies.set(cookieName, newCookie, {
    domain: '.onepeloton.com',
  });
};

// ---------------------------------------------------------------------------
// AuthUser mapper — extracts typed user from Auth0 ID token
// ---------------------------------------------------------------------------

const toAuthUser = (user: Record<string, unknown>): AuthUser => ({
  userId: (user[AUTH0_CLAIM_USER_ID] as string) ?? '',
  email: (user.email as string) ?? '',
  username: (user.preferred_username as string) ?? undefined,
  avatarUrl: (user.picture as string) ?? undefined,
});

// ---------------------------------------------------------------------------
// React Context
// ---------------------------------------------------------------------------

export const AuthContext = React.createContext<AuthContextValue>({
  status: 'init',
  isAuthenticated: false,
  isLoading: true,
  user: null,
  getAccessToken: () => Promise.resolve(null),
  refreshAccessToken: () => Promise.resolve(null),
  getIdToken: () => Promise.resolve(null),
  loginWithRedirect: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  error: null,
  returnTo: null,
});

// ---------------------------------------------------------------------------
// Save client options to localStorage for universal-logout page
// ---------------------------------------------------------------------------

export const OAUTH_CLIENT_KEY = 'OAUTH_CLIENT_OPTS';

const usePersistClientOptions = (
  clientId: string | undefined,
  domain: string | undefined,
) => {
  React.useEffect(() => {
    if (typeof window !== 'undefined' && clientId && domain) {
      try {
        window.localStorage.setItem(
          OAUTH_CLIENT_KEY,
          JSON.stringify({ domain, clientId }),
        );
      } catch {
        // Swallow — localStorage may be unavailable (private browsing)
      }
    }
  }, [clientId, domain]);
};

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------

export type AuthProviderProps = Auth0ClientOptions & {
  /**
   * Called after Auth0 redirect callback completes with the appState
   * that was passed to loginWithRedirect.
   *
   * ⚠️ This callback MUST NOT navigate (e.g. window.location.assign).
   * Navigation is owned by CallbackHandler which needs to sanitize the URL
   * and optionally POST a legacy session before redirecting.
   * Use this hook for analytics, state hydration, or logging only.
   */
  onRedirectCallback?: (appState?: Record<string, unknown>) => void;
  /** Optional error handler for Auth0 operations */
  onError?: (error: unknown, action: string) => void;
  /**
   * Optional funnel-event hook for observability. Called at lifecycle
   * checkpoints (init, silent auth, callback, login/logout) so consumer
   * apps can pipe them into Datadog RUM (`addAction`) or any other
   * analytics sink without coupling this package to a vendor.
   *
   * Event names are stable strings — see {@link AuthProviderEvent}.
   * The optional `ctx` payload is event-specific (e.g. `{ durationMs }`,
   * `{ reason }`) and MUST NOT contain PII.
   */
  onAuthEvent?: (event: AuthProviderEvent, ctx?: Record<string, unknown>) => void;
  /** Auth environment — controls global_logout URL and logout cookie name. Defaults to Prod. */
  authEnv?: AuthEnv;
  /**
   * Upper bound on how long a request arriving before initialization completes
   * will wait for the Auth0 client. Defaults to
   * {@link DEFAULT_AUTH_INIT_WAIT_MS}. Exposed for tests and for apps that
   * want to trade a longer cold-load wait for fewer guest requests.
   */
  authInitWaitMs?: number;
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  onRedirectCallback,
  onError,
  onAuthEvent,
  authEnv = AuthEnv.Prod,
  // Destructured so it stays out of `...initOptions` and is never forwarded to
  // the Auth0 client as an unknown option.
  authInitWaitMs = DEFAULT_AUTH_INIT_WAIT_MS,
  ...initOptions
}) => {
  const onAuthEventRef = React.useRef(onAuthEvent);
  onAuthEventRef.current = onAuthEvent;
  const emit = React.useCallback(
    (event: AuthProviderEvent, ctx?: Record<string, unknown>) => {
      // Observability hook — must never break auth. If the consumer's
      // handler throws, swallow the error: monitoring is best-effort.
      try {
        onAuthEventRef.current?.(event, ctx);
      } catch {
        // no-op
      }
    },
    [],
  );
  // adding useRef to prevent calling handleRedirectCallback twice when component unmounts and remounts
  // see https://community.auth0.com/t/error-invalid-state-when-calling-handleredirectcallback-on-react-app/95329/2
  const redirectHandled = React.useRef(false);

  const [auth0Client, setAuth0Client] = React.useState<Auth0Client | null>(null);
  const [status, setStatus] = React.useState<AuthStatus>('init');
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [returnTo, setReturnTo] = React.useState<string | null>(null);

  const statusRef = React.useRef(status);
  statusRef.current = status;

  // The token paths read the client through a ref rather than through state:
  // `setAuth0Client` only reaches them on the next render, but a request
  // interceptor can call `getAccessToken()` in between. The ref is published
  // synchronously, and `authInitRef` lets a caller that arrives even earlier
  // wait for it instead of silently going out unauthenticated.
  const auth0ClientRef = React.useRef<Auth0Client | null>(null);
  const authInitRef = React.useRef<Deferred>();
  if (!authInitRef.current) {
    authInitRef.current = toDeferred();
  }

  // Circuit breaker for the per-request token path — see SILENT_AUTH_BACKOFF_MS.
  const backoffRef = React.useRef<SilentAuthBackoff>(NO_SILENT_AUTH_BACKOFF);
  const recordSilentAuthFailure = React.useCallback(() => {
    backoffRef.current = toNextBackoff(backoffRef.current);
  }, []);
  const clearSilentAuthBackoff = React.useCallback(() => {
    backoffRef.current = NO_SILENT_AUTH_BACKOFF;
  }, []);

  // Persist clientId/domain for universal-logout page
  usePersistClientOptions(initOptions.clientId, initOptions.domain);

  // ---- Initialize Auth0 + handle redirect callback ----
  React.useEffect(() => {
    const processRedirectCallback = async (client: Auth0Client) => {
      const params =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null;

      if (!params?.has('code') || !params?.has('state') || redirectHandled.current) {
        return;
      }

      redirectHandled.current = true;
      emit('callback.received');
      try {
        // Single-flight the code exchange across the
        // dual-mounted legacy + new providers so only ONE client consumes the
        // single-use code. See exchangeAuthorizationCodeOnce. Remove with
        // the legacy provider.
        const { appState } = await exchangeAuthorizationCodeOnce(() =>
          client.handleRedirectCallback(),
        );
        const resolved = resolveReturnToFromAppState(appState);
        if (resolved) {
          setReturnTo(resolved);
        }
        onRedirectCallback?.(appState);
        emit('callback.success');
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        emit('callback.error', { name: err.name });
        onError?.(e, 'handleRedirectCallback');
      }
    };

    // Legacy behavior: only call `getTokenSilently()` when the
    // `peloton_visited_sites` SSO marker cookie is present, to avoid
    // `failed_silent_auth` (FSA) errors on guest cold loads.
    //
    // TODO: this gate breaks cross-app SSO when the marker cookie is
    // absent on first cross-app navigation. Awaiting alternative
    // implementation details from the auth/identity team — see Slack:
    // https://pelotoncycle.slack.com/archives/C018H2VGGM6/p1781107448802939
    const attemptSilentAuth = async (client: Auth0Client) => {
      if (!getContainsLoginCookie(authEnv)) {
        emit('silent.skipped_no_marker_cookie');
        return;
      }
      emit('silent.attempted');
      const silentStartedAt =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      try {
        // This provider owns the silent-auth refresh for the shared Auth0
        // cache. The dual-mounted legacy provider intentionally skips its
        // own redundant getTokenSilently() call (see OauthProvider's
        // `skipSilentAuth`) so the two never race the same (single-use)
        // refresh token — see DISCO-824.
        await client.getTokenSilently();
        const silentEndedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        emit('silent.success', { durationMs: silentEndedAt - silentStartedAt });
      } catch (e) {
        recordSilentAuthFailure();
        const err = e instanceof Error ? e : new Error(String(e));
        emit('silent.failed', { name: err.name });
        onError?.(e, 'getTokenSilently');
      }
    };

    const resolveAuthState = async (client: Auth0Client) => {
      try {
        const authenticated = await client.isAuthenticated();
        if (authenticated) {
          const authedUser = await client.getUser();
          if (authedUser) {
            setUser(toAuthUser(authedUser as Record<string, unknown>));
          }
        }
        setStatus(authenticated ? 'authenticated' : 'unauthenticated');
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        setStatus('unauthenticated');
        onError?.(e, 'isAuthenticated');
      }
    };

    const initAuth0 = async () => {
      const initStartedAt =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      emit('init.start');
      setStatus('loading');

      // `finally` rather than the happy path only: anything thrown in here
      // (createAuth0Client on a misconfigured tenant, an unhandled rejection in
      // resolveAuthState) must still release waiters, or every subsequent
      // request would burn the full `authInitWaitMs` before falling through.
      try {
        const client = createAuth0Client(initOptions);

        await processRedirectCallback(client);
        await attemptSilentAuth(client);
        // Publish to the ref before the setter so a request that lands between
        // now and the re-render still sees a client.
        auth0ClientRef.current = client;
        setAuth0Client(client);
        await resolveAuthState(client);

        const initEndedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
        emit('init.complete', { durationMs: initEndedAt - initStartedAt });
      } finally {
        authInitRef.current?.resolve();
      }
    };

    initAuth0();

    // Register the LogoutCookieProvider cookie on mount
    setLogoutCookie(authEnv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Context value (stable via useMemo) ----
  /**
   * Resolve the Auth0 client for an outbound request, waiting for
   * initialization if it has not finished yet.
   *
   * This is the fix for the guest-cart class of bug: a query that mounts before
   * `initAuth0` publishes the client used to get `null` here and go out with no
   * `Authorization` header, and the server then resolved the *anonymous* cart
   * cookie — which for a member who added items while logged out is a different
   * populated cart, not an empty one. Holding the request for a moment is
   * strictly better than silently downgrading it to a guest request.
   *
   * The wait is bounded (see {@link DEFAULT_AUTH_INIT_WAIT_MS}) so a
   * pathological init cannot stall every request behind it, and the deferred is
   * resolved in a `finally` so a throwing init cannot either.
   */
  const resolveAuth0Client = React.useCallback(async (): Promise<Auth0Client | null> => {
    if (auth0ClientRef.current) {
      return auth0ClientRef.current;
    }
    // SSR/node: `initAuth0` never runs, so there is nothing to wait for.
    if (typeof window === 'undefined') {
      return null;
    }

    // Guard rather than `?.` — `Promise.race` resolves immediately on a
    // non-promise entry, so passing `undefined` here would silently turn the
    // wait into a no-op.
    const initPromise = authInitRef.current?.promise;
    if (!initPromise) {
      return null;
    }

    const waitStartedAt =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        initPromise,
        new Promise<void>(resolve => {
          timer = setTimeout(resolve, authInitWaitMs);
        }),
      ]);
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
    const waitEndedAt =
      typeof performance !== 'undefined' ? performance.now() : Date.now();

    emit('token.awaited_init', {
      durationMs: waitEndedAt - waitStartedAt,
      resolved: !!auth0ClientRef.current,
    });

    return auth0ClientRef.current;
  }, [authInitWaitMs, emit]);

  /**
   * Resolve an access token for an outbound request.
   *
   * Called once per HTTP request (Axios interceptor / Apollo link), so it is
   * built to be cheap and to never block a request on work that cannot
   * succeed. Three tiers:
   *
   *  1. `cacheMode: 'cache-only'` — reads the shared localStorage cache and
   *     performs no network I/O at all. This is the hot path for an
   *     authenticated session and, because it runs before any status check, it
   *     also serves requests fired during init (`status` still `'loading'`)
   *     from the cache that `attemptSilentAuth` just seeded.
   *  2. Auth-state gate — a *network* silent-auth attempt is only worth making
   *     for a session that plausibly has an IdP session behind it. This restores
   *     the precondition the legacy interceptors had
   *     (`@ecomm/api/useAuthorizationHeader`, `@account/api/useAuthorizationHeader`:
   *     `if (isAuthenticated) { await getAccessTokenSilently() }`), which was
   *     dropped when those call sites moved to the shared
   *     `createAuthRequestInterceptor`.
   *  3. Breaker-gated network attempt — refresh-token grant, falling back to a
   *     `prompt=none` iframe, at most once per backoff window.
   *
   * Returning `null` is always safe: the interceptor falls through to the
   * checkout-token and `withCredentials` tiers, and a genuine 401 is recovered
   * by `useOauthErrorHandler` via `refreshAccessToken` below.
   */
  const getAccessToken = React.useCallback(async (): Promise<string | null> => {
    const client = await resolveAuth0Client();
    if (!client) {
      emit('token.skipped_uninitialized');
      return null;
    }

    try {
      const cached = await client.getTokenSilently({ cacheMode: 'cache-only' });
      if (cached) {
        return cached;
      }
    } catch {
      // Treat a cache read failure as a miss and continue.
    }

    if (statusRef.current !== 'authenticated') {
      // Report the status so a spike can be attributed: 'unauthenticated' is the
      // expected guest case, whereas 'init'/'loading' means requests are racing
      // provider initialization and the cache was not yet seeded.
      emit('token.skipped_unauthenticated', { status: statusRef.current });
      return null;
    }

    if (Date.now() < backoffRef.current.blockedUntil) {
      emit('token.skipped_backoff', { failures: backoffRef.current.failures });
      return null;
    }

    emit('token.network_attempted');
    try {
      const token = (await client.getTokenSilently()) ?? null;
      if (token) {
        clearSilentAuthBackoff();
      } else {
        recordSilentAuthFailure();
      }
      return token;
    } catch {
      recordSilentAuthFailure();
      return null;
    }
  }, [resolveAuth0Client, emit, clearSilentAuthBackoff, recordSilentAuthFailure]);

  /**
   * Force a network token refresh, bypassing both the cache and the auth-state
   * gate. This is the 401-recovery entrypoint (`useOauthErrorHandler`) and the
   * only place a token fetch is worth attempting eagerly, because a 401 is
   * evidence that whatever we last sent is no longer good.
   *
   * `cacheMode: 'off'` matters: the default (`'on'`) can hand back the very
   * token that just produced the 401 — a revoked-but-unexpired token is still a
   * cache hit — so the retry 401s again and the user gets logged out. Skipping
   * the cache read forces the refresh-token grant (the SDK reads the refresh
   * token separately, so it is unaffected) or the iframe.
   *
   * Not breaker-gated: 401s are rare, already single-flighted by the caller, and
   * are exactly the signal that a fresh attempt is warranted. Outcomes are still
   * recorded, so a failure here also stands the per-request path down.
   */
  const refreshAccessToken = React.useCallback(async (): Promise<string | null> => {
    const client = await resolveAuth0Client();
    if (!client) {
      emit('token.skipped_uninitialized');
      return null;
    }
    emit('token.refresh_attempted');
    try {
      const token = (await client.getTokenSilently({ cacheMode: 'off' })) ?? null;
      if (token) {
        clearSilentAuthBackoff();
      } else {
        recordSilentAuthFailure();
      }
      return token;
    } catch {
      recordSilentAuthFailure();
      return null;
    }
  }, [resolveAuth0Client, emit, clearSilentAuthBackoff, recordSilentAuthFailure]);

  const getIdToken = React.useCallback(async (): Promise<IdToken | null> => {
    if (!auth0Client) {
      return null;
    }
    try {
      const claims = (await auth0Client.getIdTokenClaims()) as
        | (Record<string, unknown> & { __raw?: string })
        | undefined;

      if (!claims || typeof claims.__raw !== 'string') {
        return null;
      }

      const { __raw, ...rest } = claims;
      return { raw: __raw, claims: rest };
    } catch (e) {
      onError?.(e, 'getIdTokenClaims');
      return null;
    }
  }, [auth0Client, onError]);

  const loginWithRedirect = React.useCallback(
    async (returnToUrl?: string, opts?: LoginRedirectOpts) => {
      const client = auth0Client ?? createAuth0Client(initOptions);
      emit('login.redirect');
      await client.loginWithRedirect({
        ...(opts ?? {}),
        appState: returnToUrl ? { returnTo: returnToUrl } : undefined,
      });
    },
    [auth0Client, emit], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const logout = React.useCallback(
    async (returnToUrl?: string, opts?: LogoutOpts) => {
      if (!auth0Client) {
        return;
      }

      // Local-only: drop the cached tokens and return, leaving the browser
      // where it is and the IdP session intact. `returnTo` is meaningless
      // here because there is no redirect to attach it to.
      if (opts?.localOnly) {
        emit('logout.fired', { universalLogout: false, localOnly: true });
        try {
          await auth0Client.logout({ openUrl: false });
        } catch (e) {
          const err = e instanceof Error ? e : new Error(String(e));
          emit('logout.error', { name: err.name });
          onError?.(e, 'logout');
        }
        return;
      }

      const tld = SupportedTLD.Com;
      const logoutReturnTo = returnToUrl
        ? `${authEnv}${tld}/sso/global_logout?continue=${encodeURIComponent(returnToUrl)}`
        : undefined;

      emit('logout.fired', { universalLogout: !!logoutReturnTo, localOnly: false });
      try {
        await auth0Client.logout(
          logoutReturnTo ? { logoutParams: { returnTo: logoutReturnTo } } : undefined,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        emit('logout.error', { name: err.name });
        onError?.(e, 'logout');
      }
    },
    [auth0Client, authEnv, onError, emit],
  );

  const contextValue = React.useMemo<AuthContextValue>(
    () => ({
      status,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'init' || status === 'loading',
      user,
      getAccessToken,
      refreshAccessToken,
      getIdToken,
      loginWithRedirect,
      logout,
      error,
      returnTo,
    }),
    [
      status,
      user,
      getAccessToken,
      refreshAccessToken,
      getIdToken,
      loginWithRedirect,
      logout,
      error,
      returnTo,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => React.useContext(AuthContext);
